import { WorkerStage } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import {
  type CancelShiftPolicy,
  type EmergencyCreditStatsCommon,
  useGetClipboardScoreProfile,
} from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { useGetWorkerShift } from "@src/appV2/redesign/WorkerShifts/api/useGetWorkerShift";
import { findCancellationPolicy } from "@src/appV2/redesign/WorkerShifts/ShiftCancel/utils/findCancellationPolicy";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { differenceInMinutes, parseISO } from "date-fns";
import { useMemo } from "react";

export interface UseShiftCancelPageDataParams {
  shiftId: string;
  currentDate: Date;
  enabled?: boolean;
}

export interface ShiftCancelPageDataDisabled {
  enabled: false;
}
export interface ShiftCancelPageDataLoading {
  enabled: true;
  isLoading: true;
}
export interface ShiftCancelPageDataErrored {
  enabled: true;
  isLoading: false;
  isError: true;
}
export interface ShiftCancelPageDataSuccessful {
  enabled: true;
  isLoading: false;
  isError: false;
  isWorkerRestricted: boolean;
  cancellationPolicy: CancelShiftPolicy | undefined;
  currentScore: number;
  baseScoreChange: number;
  creditBenefit: number;
  newScoreBeforeCredit: number;
  finalScore: number;
  initialECStats: EmergencyCreditStatsCommon | undefined;
  finalECStats: EmergencyCreditStatsCommon | undefined;
  hasCancellationCredit: boolean;
  canUseCredit: boolean;
  isWithinECWindow: boolean;
  isBecomingRestricted: boolean;
  isFinalRestriction: boolean;
  reEnrollmentScore: number | undefined;
}
export type ShiftCancelPageData =
  | ShiftCancelPageDataDisabled
  | ShiftCancelPageDataLoading
  | ShiftCancelPageDataErrored
  | ShiftCancelPageDataSuccessful;

export function useShiftCancelPageData(params: UseShiftCancelPageDataParams): ShiftCancelPageData {
  const { shiftId, currentDate, enabled = true } = params;

  const { userId } = useDefinedWorker();

  const {
    data: shiftData,
    isLoading: isShiftLoading,
    error: shiftError,
  } = useGetWorkerShift(shiftId, { enabled });
  const shift = shiftData?.response;

  const {
    data: clipboardScoreProfile,
    isLoading: isClipboardScoreLoading,
    isError: isClipboardScoreError,
  } = useGetClipboardScoreProfile(
    userId,
    { enabled: isDefined(userId) && enabled },
    shift?.agentReq,
    shift?.facilityId
  );

  const isLoading = isShiftLoading || isClipboardScoreLoading;
  const isError = isDefined(shiftError) || isClipboardScoreError;

  // The data doesn't change after loading, so the whole thing can be memoized.
  return useMemo((): ShiftCancelPageData => {
    if (!enabled) {
      return { enabled };
    }
    if (isError) {
      return { enabled, isLoading: false, isError };
    }
    if (isLoading) {
      return { enabled, isLoading };
    }

    // If the shift isn't defined after isLoading is false, something went wrong.
    if (!isDefined(shift?.start)) {
      return { enabled, isLoading: false, isError: true };
    }

    // Already-restricted users ignore Clipboard Score and Emergency Credits.
    if (clipboardScoreProfile?.accountStatus.status === WorkerStage.RESTRICTED) {
      return {
        enabled,
        isLoading,
        isError,
        isWorkerRestricted: true,
        currentScore: 0,
        baseScoreChange: 0,
        creditBenefit: 0,
        newScoreBeforeCredit: 0,
        finalScore: 0,
        cancellationPolicy: undefined,
        initialECStats: undefined,
        finalECStats: undefined,
        hasCancellationCredit: false,
        canUseCredit: false,
        isWithinECWindow: false,
        isBecomingRestricted: false,
        isFinalRestriction: false,
        reEnrollmentScore: undefined,
      };
    }

    // Calculate lead time from shift start. This *must* match the backend calculation.
    const startDate = parseISO(shift.start);
    const leadTimeHours = differenceInMinutes(startDate, currentDate) / 60;

    const cancellationPolicy = findCancellationPolicy(
      clipboardScoreProfile?.policy?.cancelShift,
      leadTimeHours
    );

    const currentScore = clipboardScoreProfile?.clipboardScore ?? 0;
    const baseScoreChange = cancellationPolicy?.points ?? 0;
    const newScoreBeforeCredit = currentScore + baseScoreChange;

    const initialECStats = clipboardScoreProfile?.emergencyCreditStats?.cancellation;
    const hasCancellationCredit = (initialECStats?.totalAvailableCredits ?? 0) > 0;
    const scoreChangeWithCredit = cancellationPolicy?.pointsWithEmergencyCredit ?? baseScoreChange;
    const creditBenefit = scoreChangeWithCredit - baseScoreChange;
    const isWithinECWindow = leadTimeHours < 24;
    const canUseCredit = hasCancellationCredit && isWithinECWindow && creditBenefit > 0;

    const finalECStats = isDefined(initialECStats)
      ? {
          ...initialECStats,
          totalAvailableCredits: initialECStats.totalAvailableCredits - (canUseCredit ? 1 : 0),
        }
      : undefined;

    const finalScoreChange = canUseCredit ? scoreChangeWithCredit : baseScoreChange;
    const finalScore = currentScore + finalScoreChange;

    const isBecomingRestricted = finalScore <= 0 && currentScore > 0;
    const isFinalRestriction =
      isDefined(clipboardScoreProfile?.accountStatus?.restrictionDuration) &&
      clipboardScoreProfile.accountStatus.restrictionDuration ===
        clipboardScoreProfile?.policy?.finalRestriction?.duration;
    const reEnrollmentScore = clipboardScoreProfile?.restriction?.reEnrollmentScore;

    return {
      enabled,
      isLoading,
      isError,
      isWorkerRestricted: false,
      cancellationPolicy,
      currentScore,
      baseScoreChange,
      creditBenefit,
      newScoreBeforeCredit,
      finalScore,
      initialECStats,
      finalECStats,
      hasCancellationCredit,
      canUseCredit,
      isWithinECWindow,
      isBecomingRestricted,
      isFinalRestriction,
      reEnrollmentScore,
    };
  }, [enabled, isLoading, isError, shift?.start, currentDate, clipboardScoreProfile]);
}
